import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  where,
  limit,
  addDoc,
  getDoc,
  setDoc,
  serverTimestamp,
  increment,
  Timestamp,
  runTransaction,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import { 
  INVENTORY_CONDITION,
  CONDITION_LABELS,
  CONDITION_COLORS,
  validateConditionTransition,
  validateTransitionQuantity,
  calculateQuantitiesAfterTransition,
  createTransitionRecord
} from '../utils/conditionManagement';
import {
  INVENTORY_STATUS,
  STATUS_LABELS,
  STATUS_COLORS,
  calculateStatusBreakdown,
  calculateAvailabilityForDateRange
} from '../utils/statusCalculations';
import {
  validateSalesTransaction,
  calculateInventoryAfterSale,
  recordSalesTransaction
} from '../utils/salesTracking';

/**
 * Inventory Service - Handle all Firestore operations for inventory management
 */

// Collection references
const INVENTORY_COLLECTION = 'inventory';
const CONFIGURATIONS_COLLECTION = 'scaffoldConfigurations';
const RESERVATIONS_COLLECTION = 'inventoryReservations';

// Re-export condition constants for backward compatibility
export const INVENTORY_CONDITION_LEGACY = {
  NEW: 'new',
  USED: 'used',
  DAMAGED: 'damaged'
};

export const INVENTORY_STATUS_LABELS = STATUS_LABELS;
export const INVENTORY_STATUS_COLORS = STATUS_COLORS;
export { INVENTORY_CONDITION, CONDITION_LABELS, CONDITION_COLORS };
export { INVENTORY_STATUS };

// Material Types Constants (based on provided inventory data)
export const MATERIAL_TYPES = {
  ENTRY_FRAME: 'Entry Frame',
  DW_FRAME: 'DW Frame',
  SW_FRAME: 'SW Frame',
  LANDING_PLATFORM: 'Landing Platform',
  DW_PLATFORM: 'DW Platform',
  SW_PLATFORM: 'SW Platform',
  WHEELS: 'Wheels',
  HORIZONTAL_BRACE: 'Horizontal Brace',
  DIAGONAL_BRACE: 'Diagonal Brace',
  NARROW_BRACE: 'Narrow Brace',
  OUTRIGGER_2M: 'Outrigger 2m',
  OUTRIGGER_3M: 'Outrigger 3m',
  OUTRIGGER_4M: 'Outrigger 4m',
  OUTRIGGER_5M: 'Outrigger 5m',
  DW_TOEBOARD: 'DW Toeboard',
  SW_TOEBOARD: 'SW Toeboard',
  STAIRS_LADDER_8: '8 Stairs ladder',
  STAIRS_LADDER_5: '5 Stairs ladder',
  SW_GUARDFRAME: 'SW Guardframe',
  DW_GUARDFRAME: 'DW Guardframe',
  HANDRAIL: 'Handrail',
  SAFETY_CLIP: 'Safety clip',
  BLUE_CAP: 'Blue Cap',
  JACKBASE: 'Jackbase'
};

// Reservation Status Constants
export const RESERVATION_STATUS = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

export const RESERVATION_STATUS_LABELS = {
  [RESERVATION_STATUS.ACTIVE]: 'Active',
  [RESERVATION_STATUS.COMPLETED]: 'Completed',
  [RESERVATION_STATUS.CANCELLED]: 'Cancelled'
};

// Configuration Width Constants
export const CONFIGURATION_WIDTH = {
  SINGLE: 'single',
  DOUBLE: 'double'
};

/**
 * Helper function to normalize date to YYYY-MM-DD format
 * @param {string|Date} dateInput - Date input to normalize
 * @returns {string|null} Normalized date string or null if invalid
 */
const normalizeDate = (dateInput) => {
  if (!dateInput) return null;
  
  let date;
  if (typeof dateInput === 'string') {
    date = new Date(dateInput);
  } else if (dateInput instanceof Date) {
    date = new Date(dateInput);
  } else {
    return null;
  }
  
  if (isNaN(date.getTime())) return null;
  
  // Set to UTC midnight
  const utcDate = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0, 0, 0, 0
  ));
  
  return utcDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
};

/**
 * Helper function to create date range array
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Array} Array of date strings
 */
const createDateRange = (startDate, endDate) => {
  const dates = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
    dates.push(normalizeDate(date));
  }
  
  return dates;
};

/**
 * Get all inventory items with filtering options
 * @param {Object} filters - Filter options (materialType, status, limit, orderBy)
 * @returns {Promise<Array>} Array of inventory items
 */
export const getInventoryItems = async (filters = {}) => {
  try {
    let q = collection(db, INVENTORY_COLLECTION);
    
    // Add filters
    if (filters.materialType) {
      q = query(q, where('materialType', '==', filters.materialType));
    }
    
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    // Add ordering
    const orderField = filters.orderBy || 'materialType';
    const orderDirection = filters.orderDirection || 'asc';
    q = query(q, orderBy(orderField, orderDirection));
    
    // Add limit
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }

    const snapshot = await getDocs(q);
    const items = [];
    
    snapshot.forEach((doc) => {
      items.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        purchaseDate: doc.data().purchaseDate?.toDate(),
        lastMaintenanceDate: doc.data().lastMaintenanceDate?.toDate()
      });
    });
    
    return items;
  } catch (error) {
    console.error('Error getting inventory items:', error);
    return [];
  }
};

/**
 * Subscribe to inventory items with real-time updates
 * @param {Function} callback - Function to call when data updates
 * @param {Object} filters - Filter options
 * @returns {Function} Unsubscribe function
 */
export const subscribeToInventoryItems = (callback, filters = {}) => {
  try {
    let q = collection(db, INVENTORY_COLLECTION);
    
    if (filters.materialType) {
      q = query(q, where('materialType', '==', filters.materialType));
    }
    
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    const orderField = filters.orderBy || 'materialType';
    const orderDirection = filters.orderDirection || 'asc';
    q = query(q, orderBy(orderField, orderDirection));
    
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }

    return onSnapshot(q, (snapshot) => {
      const items = [];
      snapshot.forEach((doc) => {
        items.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
          purchaseDate: doc.data().purchaseDate?.toDate(),
          lastMaintenanceDate: doc.data().lastMaintenanceDate?.toDate()
        });
      });
      callback(items);
    }, (error) => {
      console.error('Error fetching inventory items:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up inventory subscription:', error);
    callback([]);
    return () => {};
  }
};

/**
 * Create or update inventory item (quantity-based)
 * @param {Object} itemData - Inventory item data with quantities
 * @returns {Promise<Object>} Created/updated item with ID
 */
export const createInventoryItem = async (itemData) => {
  try {
    // Check if item with same material type already exists
    const existingItems = await getInventoryItems({ materialType: itemData.materialType });
    
    if (existingItems.length > 0) {
      // Update existing item quantities
      const existingItem = existingItems[0];
      const updatedQuantities = {
        totalQuantity: (existingItem.totalQuantity || 0) + (itemData.totalQuantity || 0),
        newQuantity: (existingItem.newQuantity || 0) + (itemData.newQuantity || 0),
        usedQuantity: (existingItem.usedQuantity || 0) + (itemData.usedQuantity || 0),
        damagedQuantity: (existingItem.damagedQuantity || 0) + (itemData.damagedQuantity || 0),
        updatedAt: serverTimestamp()
      };
      
      await updateInventoryItem(existingItem.id, updatedQuantities);
      
      return {
        id: existingItem.id,
        ...existingItem,
        ...updatedQuantities
      };
    } else {
      // Create new item
      const item = {
        materialType: itemData.materialType,
        totalQuantity: itemData.totalQuantity || 0,
        newQuantity: itemData.newQuantity || 0,
        usedQuantity: itemData.usedQuantity || 0,
        damagedQuantity: itemData.damagedQuantity || 0,
        notes: itemData.notes || '',
        purchaseDate: itemData.purchaseDate || null,
        lastMaintenanceDate: itemData.lastMaintenanceDate || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, INVENTORY_COLLECTION), item);
      
      return {
        id: docRef.id,
        ...item
      };
    }
  } catch (error) {
    console.error('Error creating inventory item:', error);
    throw error;
  }
};

/**
 * Update an existing inventory item
 * @param {string} itemId - Item ID
 * @param {Object} updates - Updated fields
 * @returns {Promise<boolean>} Success status
 */
export const updateInventoryItem = async (itemId, updates) => {
  try {
    const itemRef = doc(db, INVENTORY_COLLECTION, itemId);
    await updateDoc(itemRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating inventory item:', error);
    return false;
  }
};

/**
 * Delete an inventory item
 * @param {string} itemId - Item ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteInventoryItem = async (itemId) => {
  try {
    await deleteDoc(doc(db, INVENTORY_COLLECTION, itemId));
    return true;
  } catch (error) {
    console.error('Error deleting inventory item:', error);
    return false;
  }
};

/**
 * Bulk import inventory items
 * @param {Array} itemsArray - Array of inventory items to import
 * @returns {Promise<Object>} Import results
 */
export const bulkImportInventory = async (itemsArray) => {
  try {
    const batch = writeBatch(db);
    let imported = 0;
    let errors = 0;
    
    for (const itemData of itemsArray) {
      try {
        const docRef = doc(collection(db, INVENTORY_COLLECTION));
        const item = {
          ...itemData,
          status: itemData.status || INVENTORY_STATUS.NEW,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        batch.set(docRef, item);
        imported++;
      } catch (error) {
        console.error('Error preparing item for import:', error);
        errors++;
      }
    }
    
    await batch.commit();
    
    return {
      success: true,
      imported,
      errors,
      total: itemsArray.length
    };
  } catch (error) {
    console.error('Error bulk importing inventory:', error);
    return {
      success: false,
      imported: 0,
      errors: itemsArray.length,
      total: itemsArray.length,
      error: error.message
    };
  }
};

/**
 * Get inventory statistics (quantity-based)
 * @returns {Promise<Object>} Statistics object
 */
export const getInventoryStats = async () => {
  try {
    const items = await getInventoryItems();
    
    let totalQuantity = 0;
    let newQuantity = 0;
    let usedQuantity = 0;
    let damagedQuantity = 0;
    
    const byMaterialType = {};
    
    // Calculate totals and by material type
    items.forEach(item => {
      const itemTotal = item.totalQuantity || 0;
      const itemNew = item.newQuantity || 0;
      const itemUsed = item.usedQuantity || 0;
      const itemDamaged = item.damagedQuantity || 0;
      
      totalQuantity += itemTotal;
      newQuantity += itemNew;
      usedQuantity += itemUsed;
      damagedQuantity += itemDamaged;
      
      byMaterialType[item.materialType] = {
        totalQuantity: itemTotal,
        newQuantity: itemNew,
        usedQuantity: itemUsed,
        damagedQuantity: itemDamaged,
        availableQuantity: itemNew + itemUsed
      };
    });
    
    // Add material types with zero quantities
    Object.values(MATERIAL_TYPES).forEach(materialType => {
      if (!byMaterialType[materialType]) {
        byMaterialType[materialType] = {
          totalQuantity: 0,
          newQuantity: 0,
          usedQuantity: 0,
          damagedQuantity: 0,
          availableQuantity: 0
        };
      }
    });
    
    return {
      totalQuantity,
      newQuantity,
      usedQuantity,
      damagedQuantity,
      availableQuantity: newQuantity + usedQuantity,
      materialTypeCount: items.length,
      byMaterialType
    };
  } catch (error) {
    console.error('Error getting inventory statistics:', error);
    return {
      totalQuantity: 0,
      newQuantity: 0,
      usedQuantity: 0,
      damagedQuantity: 0,
      availableQuantity: 0,
      materialTypeCount: 0,
      byMaterialType: {}
    };
  }
};

/**
 * Update item status with reason tracking
 * @param {string} itemId - Item ID
 * @param {string} newStatus - New status
 * @param {string} reason - Reason for status change
 * @returns {Promise<boolean>} Success status
 */
export const updateItemStatus = async (itemId, newStatus, reason = '') => {
  try {
    const updates = {
      status: newStatus,
      lastStatusChange: serverTimestamp(),
      statusChangeReason: reason,
      updatedAt: serverTimestamp()
    };
    
    // Update maintenance date if item is being marked as damaged
    if (newStatus === INVENTORY_STATUS.DAMAGED) {
      updates.lastMaintenanceDate = serverTimestamp();
    }
    
    return await updateInventoryItem(itemId, updates);
  } catch (error) {
    console.error('Error updating item status:', error);
    return false;
  }
};

/**
 * Get available quantities for a specific material type and date range
 * @param {string} materialType - Material type to check
 * @param {number} quantity - Required quantity
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Availability information
 */
export const checkMaterialAvailability = async (materialType, quantity, startDate, endDate) => {
  try {
    // Get inventory item for this material type
    const items = await getInventoryItems({ materialType });
    
    if (items.length === 0) {
      return {
        materialType,
        totalQuantity: 0,
        newQuantity: 0,
        usedQuantity: 0,
        damagedQuantity: 0,
        availableQuantity: 0,
        reservedQuantity: 0,
        requiredQuantity: quantity,
        shortage: quantity,
        isAvailable: false
      };
    }
    
    const inventoryItem = items[0]; // Should only be one item per material type now
    
    // Calculate available quantity (new + used, excluding damaged)
    const totalAvailable = (inventoryItem.newQuantity || 0) + (inventoryItem.usedQuantity || 0);
    
    // Get active reservations that overlap with the requested date range
    const overlappingReservations = await getOverlappingReservations(startDate, endDate);
    
    // Calculate reserved quantities for this material type
    let reservedQuantity = 0;
    overlappingReservations.forEach(reservation => {
      const materialReservation = reservation.reservedItems?.find(
        item => item.materialType === materialType
      );
      if (materialReservation) {
        reservedQuantity += materialReservation.quantity || 0;
      }
    });
    
    const availableQuantity = Math.max(0, totalAvailable - reservedQuantity);
    const shortage = Math.max(0, quantity - availableQuantity);
    
    return {
      materialType,
      totalQuantity: inventoryItem.totalQuantity || 0,
      newQuantity: inventoryItem.newQuantity || 0,
      usedQuantity: inventoryItem.usedQuantity || 0,
      damagedQuantity: inventoryItem.damagedQuantity || 0,
      availableQuantity,
      reservedQuantity,
      requiredQuantity: quantity,
      shortage,
      isAvailable: shortage === 0
    };
  } catch (error) {
    console.error('Error checking material availability:', error);
    return {
      materialType,
      totalQuantity: 0,
      newQuantity: 0,
      usedQuantity: 0,
      damagedQuantity: 0,
      availableQuantity: 0,
      reservedQuantity: 0,
      requiredQuantity: quantity,
      shortage: quantity,
      isAvailable: false
    };
  }
};

/**
 * Get overlapping reservations for a date range (includes both inventory reservations and job schedules)
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Array>} Array of overlapping reservations
 */
export const getOverlappingReservations = async (startDate, endDate) => {
  try {
    // Get traditional inventory reservations
    const reservations = await getActiveReservations();
    
    const overlappingReservations = reservations.filter(reservation => {
      const resStart = reservation.startDate;
      const resEnd = reservation.endDate;
      
      // Check if date ranges overlap
      return (
        (startDate <= resEnd && endDate >= resStart) ||
        (resStart <= endDate && resEnd >= startDate)
      );
    });

    // IMPORTANT: Also get job schedules that overlap with the date range
    // Job schedules represent actual material usage and should be counted as reservations
    try {
      const { getSchedules } = await import('./scheduleService');
      
      // Get all schedules that overlap with the date range
      const schedules = await getSchedules({ 
        includePastDates: true,
        startDate: new Date(startDate),
        endDate: new Date(endDate)
      });

      console.log(`📅 Found ${schedules.length} job schedules overlapping with ${startDate} to ${endDate}`);

          // Convert job schedules to reservation format for consistency
          const scheduleReservations = [];
          
          for (const schedule of schedules) {
            // Only include confirmed schedules that have actual dates
            const scheduleDate = schedule.scheduledDate || schedule.jobDate;
            if (scheduleDate && 
                schedule.status !== 'cancelled' && 
                scheduleDate >= startDate && 
                scheduleDate <= endDate) {
              
              // Convert schedule to reservation format
              // Estimate material requirements based on configuration
              // This is a simplified approach - in a full implementation, you'd want to
              // calculate exact material requirements from the schedule's configuration
              const reservedItems = await estimateMaterialRequirementsFromSchedule(schedule);

              scheduleReservations.push({
                id: `schedule_${schedule.id}`,
                type: 'job_schedule',
                invoiceId: schedule.invoiceId,
                invoiceNumber: schedule.invoiceNumber,
                startDate: scheduleDate,
                endDate: scheduleDate, // Job schedules are typically single-day
                status: 'active',
                reservedItems: reservedItems,
                jobType: schedule.jobType,
                customerName: schedule.customerName,
                siteAddress: schedule.siteAddress
              });
            }
          }

      console.log(`📦 Converted ${scheduleReservations.length} job schedules to reservation format`);

      // ENHANCED DEDUPLICATION: Intelligently merge traditional reservations with job schedules
      // Priority: Job schedules (operational reality) > Traditional reservations (estimates)
      const mergedReservations = intelligentlyMergeReservations(overlappingReservations, scheduleReservations);
      
      console.log(` Total overlapping reservations: ${mergedReservations.length} (${overlappingReservations.length} traditional + ${scheduleReservations.length} from schedules → ${mergedReservations.length} after intelligent merge)`);
      
      return mergedReservations;

    } catch (scheduleError) {
      console.error('Error loading job schedules for reservation calculation:', scheduleError);
      // Fall back to just traditional reservations if schedule loading fails
      return overlappingReservations;
    }
    
  } catch (error) {
    console.error('Error getting overlapping reservations:', error);
    return [];
  }
};

/**
 * Intelligently merge traditional reservations with job schedule reservations
 * Priority: Job schedules (operational reality) > Traditional reservations (estimates)
 * Enhanced to handle multiple schedules per invoice and prevent duplicates
 * @param {Array} traditionalReservations - Traditional inventory reservations
 * @param {Array} scheduleReservations - Job schedule-based reservations
 * @returns {Array} Merged and deduplicated reservations
 */
const intelligentlyMergeReservations = (traditionalReservations, scheduleReservations) => {
  const mergedReservations = [];
  const processedInvoiceIds = new Set();
  const invoiceReservationMap = new Map(); // Track reservations by invoice for better deduplication
  
  console.log(`🔄 Starting enhanced intelligent merge: ${traditionalReservations.length} traditional + ${scheduleReservations.length} schedule reservations`);
  
  // Step 1: Group job schedule reservations by invoice ID to handle multiple schedules per invoice
  const schedulesByInvoice = new Map();
  const standaloneSchedules = [];
  
  scheduleReservations.forEach(scheduleRes => {
    if (scheduleRes.invoiceId && scheduleRes.invoiceNumber) {
      const invoiceKey = scheduleRes.invoiceId;
      if (!schedulesByInvoice.has(invoiceKey)) {
        schedulesByInvoice.set(invoiceKey, []);
      }
      schedulesByInvoice.get(invoiceKey).push(scheduleRes);
    } else {
      standaloneSchedules.push(scheduleRes);
    }
  });
  
  console.log(`📊 Schedule grouping: ${schedulesByInvoice.size} invoices with schedules, ${standaloneSchedules.length} standalone schedules`);
  
  // Step 2: Process grouped job schedules (merge multiple schedules per invoice)
  schedulesByInvoice.forEach((schedules, invoiceId) => {
    if (schedules.length === 1) {
      // Single schedule for this invoice - process normally
      const scheduleRes = schedules[0];
      const mergedReservation = {
        ...scheduleRes,
        source: 'job_schedule',
        priority: 'high',
        dateAccuracy: 'operational'
      };
      
      mergedReservations.push(mergedReservation);
      processedInvoiceIds.add(invoiceId);
      invoiceReservationMap.set(invoiceId, mergedReservation);
      
      console.log(`✅ Added single job schedule reservation for invoice ${scheduleRes.invoiceNumber}`);
    } else {
      // Multiple schedules for same invoice - merge them intelligently
      console.log(`🔄 Merging ${schedules.length} schedules for invoice ${schedules[0].invoiceNumber}`);
      
      const primarySchedule = schedules[0]; // Use first schedule as base
      const allMaterials = new Map();
      const allDates = [];
      
      // Collect all materials and dates from all schedules
      schedules.forEach((schedule, index) => {
        const scheduleDate = schedule.startDate || schedule.scheduledDate || schedule.jobDate;
        if (scheduleDate) {
          allDates.push(scheduleDate);
        }
        
        // Merge materials from all schedules
        if (schedule.reservedItems && schedule.reservedItems.length > 0) {
          schedule.reservedItems.forEach(item => {
            const existing = allMaterials.get(item.materialType);
            if (existing) {
              // Use higher quantity if there's a conflict
              if (item.quantity > existing.quantity) {
                allMaterials.set(item.materialType, {
                  ...item,
                  source: `schedule_${index + 1}_higher`,
                  originalQuantities: [...(existing.originalQuantities || [existing.quantity]), item.quantity]
                });
              } else {
                existing.originalQuantities = existing.originalQuantities || [existing.quantity];
                existing.originalQuantities.push(item.quantity);
              }
            } else {
              allMaterials.set(item.materialType, {
                ...item,
                source: `schedule_${index + 1}`,
                originalQuantities: [item.quantity]
              });
            }
          });
        }
      });
      
      // Create merged reservation
      const mergedReservation = {
        ...primarySchedule,
        id: `merged_schedule_${invoiceId}`,
        source: 'job_schedule_merged',
        priority: 'high',
        dateAccuracy: 'operational',
        startDate: allDates.length > 0 ? Math.min(...allDates) : primarySchedule.startDate,
        endDate: allDates.length > 0 ? Math.max(...allDates) : primarySchedule.endDate,
        reservedItems: Array.from(allMaterials.values()),
        mergedFromSchedules: schedules.length,
        originalScheduleIds: schedules.map(s => s.id)
      };
      
      mergedReservations.push(mergedReservation);
      processedInvoiceIds.add(invoiceId);
      invoiceReservationMap.set(invoiceId, mergedReservation);
      
      console.log(`✅ Created merged job schedule reservation for invoice ${primarySchedule.invoiceNumber} (${schedules.length} schedules → 1 reservation)`);
      console.log(`   Materials: ${mergedReservation.reservedItems.length} types, Date range: ${mergedReservation.startDate} to ${mergedReservation.endDate}`);
    }
  });
  
  // Step 3: Process standalone schedules (no invoice ID)
  standaloneSchedules.forEach(scheduleRes => {
    mergedReservations.push({
      ...scheduleRes,
      source: 'job_schedule_standalone',
      priority: 'medium',
      dateAccuracy: 'operational'
    });
    console.log(`✅ Added standalone job schedule reservation ${scheduleRes.id}`);
  });
  
  // Step 4: Process traditional reservations (only if no job schedule exists for same invoice)
  traditionalReservations.forEach(traditionalRes => {
    const invoiceKey = traditionalRes.invoiceId;
    
    if (invoiceKey && processedInvoiceIds.has(invoiceKey)) {
      // Job schedule already exists for this invoice - enhance existing reservation with traditional data
      const existingReservation = invoiceReservationMap.get(invoiceKey);
      
      if (existingReservation && traditionalRes.reservedItems && traditionalRes.reservedItems.length > 0) {
        // Merge material lists if traditional reservation has more detailed materials
        const mergedMaterials = mergeMaterialLists(
          existingReservation.reservedItems || [],
          traditionalRes.reservedItems || []
        );
        
        const originalMaterialCount = existingReservation.reservedItems?.length || 0;
        if (mergedMaterials.length > originalMaterialCount) {
          existingReservation.reservedItems = mergedMaterials;
          existingReservation.materialSource = 'enhanced_with_traditional';
          existingReservation.enhancedMaterialCount = mergedMaterials.length - originalMaterialCount;
          console.log(`🔄 Enhanced job schedule reservation for invoice ${traditionalRes.invoiceNumber} (+${existingReservation.enhancedMaterialCount} materials from traditional)`);
        }
      }
    } else {
      // No job schedule exists for this invoice - use traditional reservation
      const traditionalReservation = {
        ...traditionalRes,
        source: 'traditional',
        priority: 'low',
        dateAccuracy: 'estimated'
      };
      
      mergedReservations.push(traditionalReservation);
      
      if (invoiceKey) {
        processedInvoiceIds.add(invoiceKey);
        invoiceReservationMap.set(invoiceKey, traditionalReservation);
      }
      console.log(`✅ Added traditional reservation for invoice ${traditionalRes.invoiceNumber || 'N/A'} (estimated date: ${traditionalRes.startDate})`);
    }
  });
  
  // Step 5: Final deduplication check - ensure no duplicate invoice IDs remain
  const finalReservations = [];
  const finalInvoiceCheck = new Set();
  
  mergedReservations.forEach(reservation => {
    const invoiceKey = reservation.invoiceId;
    
    if (!invoiceKey) {
      // No invoice ID - add as is (standalone reservations)
      finalReservations.push(reservation);
    } else if (!finalInvoiceCheck.has(invoiceKey)) {
      // First reservation for this invoice - add it
      finalReservations.push(reservation);
      finalInvoiceCheck.add(invoiceKey);
    } else {
      // Duplicate detected - this should not happen with improved logic, but log it
      console.warn(`⚠️ Duplicate reservation detected for invoice ${reservation.invoiceNumber} (${invoiceKey}) - skipping duplicate`);
    }
  });
  
  // Step 6: Sort by date and priority
  finalReservations.sort((a, b) => {
    // First sort by date
    const dateComparison = (a.startDate || '').localeCompare(b.startDate || '');
    if (dateComparison !== 0) return dateComparison;
    
    // Then by priority (high > medium > low)
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
  });
  
  console.log(`📊 Enhanced intelligent merge complete: ${finalReservations.length} final reservations`);
  console.log(`   - Job schedule reservations: ${finalReservations.filter(r => r.source?.startsWith('job_schedule')).length}`);
  console.log(`   - Traditional reservations: ${finalReservations.filter(r => r.source === 'traditional').length}`);
  console.log(`   - Enhanced with merged materials: ${finalReservations.filter(r => r.materialSource === 'enhanced_with_traditional').length}`);
  console.log(`   - Merged from multiple schedules: ${finalReservations.filter(r => r.source === 'job_schedule_merged').length}`);
  
  // Final validation - check for any remaining duplicates
  const invoiceIds = finalReservations.map(r => r.invoiceId).filter(Boolean);
  const uniqueInvoiceIds = [...new Set(invoiceIds)];
  const duplicateCount = invoiceIds.length - uniqueInvoiceIds.length;
  
  if (duplicateCount > 0) {
    console.error(`❌ CRITICAL: ${duplicateCount} duplicate reservations still exist after deduplication!`);
  } else {
    console.log(`✅ SUCCESS: No duplicate reservations detected - deduplication working perfectly`);
  }
  
  return finalReservations;
};

/**
 * Merge material lists from different sources, preferring higher quantities
 * @param {Array} scheduleItems - Material items from job schedule
 * @param {Array} traditionalItems - Material items from traditional reservation
 * @returns {Array} Merged material list
 */
const mergeMaterialLists = (scheduleItems, traditionalItems) => {
  const mergedItems = new Map();
  
  // Add schedule items first
  scheduleItems.forEach(item => {
    mergedItems.set(item.materialType, {
      ...item,
      source: 'schedule'
    });
  });
  
  // Merge traditional items, using higher quantities
  traditionalItems.forEach(item => {
    const existing = mergedItems.get(item.materialType);
    if (existing) {
      // Use higher quantity and merge sources
      if (item.quantity > existing.quantity) {
        mergedItems.set(item.materialType, {
          ...item,
          source: 'traditional_higher',
          originalScheduleQuantity: existing.quantity
        });
      } else {
        existing.source = 'schedule_higher';
        existing.originalTraditionalQuantity = item.quantity;
      }
    } else {
      // New material type from traditional reservation
      mergedItems.set(item.materialType, {
        ...item,
        source: 'traditional_only'
      });
    }
  });
  
  return Array.from(mergedItems.values());
};

/**
 * Estimate material requirements from a job schedule
 * Enhanced to use actual material list data when available
 * @param {Object} schedule - Job schedule object
 * @returns {Array} Array of estimated material requirements
 */
const estimateMaterialRequirementsFromSchedule = async (schedule) => {
  const reservedItems = [];
  
  try {
    console.log(`📦 Estimating materials for schedule ${schedule.id} (Invoice: ${schedule.invoiceNumber})`);
    
    // PRIORITY 1: Try to get actual material list from the invoice
    if (schedule.invoiceId || schedule.invoiceNumber) {
      try {
        // Try to get material list from material list service
        const { getMaterialListByInvoice } = await import('./materialListService');
        const materialList = await getMaterialListByInvoice(schedule.invoiceId || schedule.invoiceNumber);
        
        if (materialList && materialList.requirements) {
          console.log(`✅ Using actual material list for schedule ${schedule.invoiceNumber}`);
          
          // Convert material list requirements to reservation format
          Object.entries(materialList.requirements).forEach(([materialType, requirement]) => {
            const quantity = requirement.totalQuantity || requirement.quantity || 0;
            if (quantity > 0) {
              reservedItems.push({
                materialType,
                quantity,
                source: 'material_list'
              });
            }
          });
          
          const totalQuantity = reservedItems.reduce((sum, item) => sum + item.quantity, 0);
          console.log(`📊 Material list total: ${totalQuantity} items (${reservedItems.length} types)`);
          
          return reservedItems;
        }
      } catch (error) {
        console.log(`⚠️ Could not load material list for ${schedule.invoiceNumber}, falling back to estimation`);
      }
      
      // PRIORITY 2: Try to get from invoice service
      try {
        const { getInvoiceByNumber } = await import('./invoiceService');
        const invoice = await getInvoiceByNumber(schedule.invoiceNumber);
        
        if (invoice && invoice.lineItems && Array.isArray(invoice.lineItems)) {
          console.log(`✅ Using invoice line items for schedule ${schedule.invoiceNumber}`);
          
          // Use actual line items from invoice
          invoice.lineItems.forEach(item => {
            if (item.materialType && item.quantity) {
              reservedItems.push({
                materialType: item.materialType,
                quantity: parseInt(item.quantity) || 0,
                source: 'invoice_line_items'
              });
            }
          });
          
          const totalQuantity = reservedItems.reduce((sum, item) => sum + item.quantity, 0);
          console.log(`📊 Invoice line items total: ${totalQuantity} items (${reservedItems.length} types)`);
          
          return reservedItems;
        }
      } catch (error) {
        console.log(`⚠️ Could not load invoice for ${schedule.invoiceNumber}, falling back to configuration estimation`);
      }
    }
    
    // PRIORITY 3: Fall back to configuration-based estimation
    console.log(`📝 Using configuration-based estimation for schedule ${schedule.id}`);
    
    const configuration = schedule.configuration || '';
    
    // Look for platform mentions (e.g., "2m Platform Single", "3m Platform Double")
    const platformMatches = configuration.match(/(\d+)m\s+Platform\s+(Single|Double)/gi);
    
    if (platformMatches && platformMatches.length > 0) {
      platformMatches.forEach(match => {
        const heightMatch = match.match(/(\d+)m/);
        const widthMatch = match.match(/(Single|Double)/i);
        
        if (heightMatch && widthMatch) {
          const platformHeight = parseInt(heightMatch[1]);
          const width = widthMatch[1].toLowerCase();
          
          // Estimate basic material requirements based on configuration
          if (width === 'single') {
            reservedItems.push(
              { materialType: MATERIAL_TYPES.SW_FRAME, quantity: Math.max(2, platformHeight), source: 'config_estimation' },
              { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: Math.max(1, Math.floor(platformHeight / 2)), source: 'config_estimation' },
              { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: Math.max(4, platformHeight * 2), source: 'config_estimation' },
              { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: Math.max(2, platformHeight), source: 'config_estimation' }
            );
          } else if (width === 'double') {
            reservedItems.push(
              { materialType: MATERIAL_TYPES.DW_FRAME, quantity: Math.max(2, platformHeight), source: 'config_estimation' },
              { materialType: MATERIAL_TYPES.DW_PLATFORM, quantity: Math.max(1, Math.floor(platformHeight / 2)), source: 'config_estimation' },
              { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: Math.max(6, platformHeight * 3), source: 'config_estimation' },
              { materialType: MATERIAL_TYPES.DIAGONAL_BRACE, quantity: Math.max(2, platformHeight), source: 'config_estimation' }
            );
          }
          
          // Add common materials for both types
          reservedItems.push(
            { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: Math.max(8, platformHeight * 4), source: 'config_estimation' },
            { materialType: MATERIAL_TYPES.WHEELS, quantity: 4, source: 'config_estimation' }
          );
          
          // Add outriggers for taller scaffolds
          if (platformHeight >= 3) {
            reservedItems.push(
              { materialType: MATERIAL_TYPES.OUTRIGGER_2M, quantity: 4, source: 'config_estimation' }
            );
          }
        }
      });
    }
    
    // If no specific configuration found, add minimal default reservation
    if (reservedItems.length === 0) {
      console.log(`⚠️ No specific configuration found for schedule ${schedule.id}, using minimal default`);
      reservedItems.push(
        { materialType: MATERIAL_TYPES.SW_FRAME, quantity: 2, source: 'default_estimation' },
        { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: 1, source: 'default_estimation' },
        { materialType: MATERIAL_TYPES.HORIZONTAL_BRACE, quantity: 4, source: 'default_estimation' },
        { materialType: MATERIAL_TYPES.SAFETY_CLIP, quantity: 8, source: 'default_estimation' }
      );
    }
    
    const totalQuantity = reservedItems.reduce((sum, item) => sum + item.quantity, 0);
    console.log(`📦 Estimated ${totalQuantity} items (${reservedItems.length} types) for schedule ${schedule.invoiceNumber}: ${configuration}`);
    
  } catch (error) {
    console.error('Error estimating material requirements from schedule:', error);
    // Return minimal default if estimation fails
    return [
      { materialType: MATERIAL_TYPES.SW_FRAME, quantity: 2, source: 'error_fallback' },
      { materialType: MATERIAL_TYPES.SW_PLATFORM, quantity: 1, source: 'error_fallback' }
    ];
  }
  
  return reservedItems;
};

/**
 * Get active reservations
 * @param {Object} filters - Filter options
 * @returns {Promise<Array>} Array of active reservations
 */
export const getActiveReservations = async (filters = {}) => {
  try {
    let q = collection(db, RESERVATIONS_COLLECTION);
    
    // Only get active reservations by default
    if (!filters.includeAll) {
      q = query(q, where('status', '==', RESERVATION_STATUS.ACTIVE));
    }
    
    if (filters.invoiceId) {
      q = query(q, where('invoiceId', '==', filters.invoiceId));
    }
    
    q = query(q, orderBy('startDate', 'asc'));
    
    const snapshot = await getDocs(q);
    const reservations = [];
    
    snapshot.forEach((doc) => {
      reservations.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      });
    });
    
    return reservations;
  } catch (error) {
    console.error('Error getting active reservations:', error);
    return [];
  }
};

/**
 * Create a reservation for inventory items
 * @param {Object} reservationData - Reservation data
 * @returns {Promise<Object>} Created reservation with ID
 */
export const createReservation = async (reservationData) => {
  try {
    const reservation = {
      ...reservationData,
      status: RESERVATION_STATUS.ACTIVE,
      startDate: normalizeDate(reservationData.startDate),
      endDate: normalizeDate(reservationData.endDate),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, RESERVATIONS_COLLECTION), reservation);
    
    console.log(`✅ Created reservation ${docRef.id} for invoice ${reservationData.invoiceNumber}`);
    
    return {
      id: docRef.id,
      ...reservation
    };
  } catch (error) {
    console.error('Error creating reservation:', error);
    throw error;
  }
};

/**
 * Update reservation status
 * @param {string} reservationId - Reservation ID
 * @param {string} newStatus - New status
 * @returns {Promise<boolean>} Success status
 */
export const updateReservationStatus = async (reservationId, newStatus) => {
  try {
    const reservationRef = doc(db, RESERVATIONS_COLLECTION, reservationId);
    await updateDoc(reservationRef, {
      status: newStatus,
      updatedAt: serverTimestamp()
    });
    
    console.log(`✅ Updated reservation ${reservationId} status to ${newStatus}`);
    return true;
  } catch (error) {
    console.error('Error updating reservation status:', error);
    return false;
  }
};

/**
 * Release a reservation (mark as completed)
 * @param {string} reservationId - Reservation ID
 * @returns {Promise<boolean>} Success status
 */
export const releaseReservation = async (reservationId) => {
  return await updateReservationStatus(reservationId, RESERVATION_STATUS.COMPLETED);
};

/**
 * Cancel a reservation
 * @param {string} reservationId - Reservation ID
 * @returns {Promise<boolean>} Success status
 */
export const cancelReservation = async (reservationId) => {
  return await updateReservationStatus(reservationId, RESERVATION_STATUS.CANCELLED);
};

/**
 * Delete reservations by invoice ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Result with count of deleted reservations
 */
export const deleteReservationsByInvoiceId = async (invoiceId) => {
  try {
    console.log(`🗑️ Deleting reservations for invoice: ${invoiceId}`);
    
    const q = query(
      collection(db, RESERVATIONS_COLLECTION),
      where('invoiceId', '==', invoiceId)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log(`✅ No reservations found for invoice ${invoiceId}`);
      return { success: true, deletedCount: 0 };
    }
    
    console.log(`📅 Found ${snapshot.size} reservation(s) to delete for invoice ${invoiceId}`);
    
    const deletePromises = [];
    snapshot.forEach((doc) => {
      console.log(`🗑️ Deleting reservation: ${doc.id}`);
      deletePromises.push(deleteDoc(doc.ref));
    });
    
    await Promise.all(deletePromises);
    
    console.log(`✅ Successfully deleted ${snapshot.size} reservation(s) for invoice ${invoiceId}`);
    
    return { 
      success: true, 
      deletedCount: snapshot.size,
      message: `Deleted ${snapshot.size} reservation(s)`
    };
    
  } catch (error) {
    console.error(`❌ Error deleting reservations for invoice ${invoiceId}:`, error);
    return { 
      success: false, 
      deletedCount: 0,
      error: error.message 
    };
  }
};

/**
 * Search inventory items
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Filtered inventory items
 */
export const searchInventoryItems = async (searchTerm) => {
  try {
    const items = await getInventoryItems();
    const searchLower = searchTerm.toLowerCase();
    
    return items.filter(item => 
      item.materialType?.toLowerCase().includes(searchLower) ||
      item.serialNumber?.toLowerCase().includes(searchLower) ||
      item.notes?.toLowerCase().includes(searchLower)
    );
  } catch (error) {
    console.error('Error searching inventory items:', error);
    return [];
  }
};

/**
 * Transition item quantities between conditions
 * @param {string} materialType - Material type
 * @param {string} fromCondition - Source condition
 * @param {string} toCondition - Target condition
 * @param {number} quantity - Quantity to transition
 * @param {string} reason - Reason for transition
 * @param {Object} options - Additional options (updatedBy, jobId, etc.)
 * @returns {Promise<Object>} Transition result
 */
export const transitionItemCondition = async (materialType, fromCondition, toCondition, quantity, reason, options = {}) => {
  try {
    // Get current inventory
    const items = await getInventoryItems({ materialType });
    if (items.length === 0) {
      throw new Error(`No inventory found for material type: ${materialType}`);
    }
    
    const currentInventory = items[0];
    
    // Validate transition
    const transitionValidation = validateConditionTransition(fromCondition, toCondition);
    if (!transitionValidation.isValid) {
      throw new Error(transitionValidation.error);
    }
    
    // Validate quantity
    const quantityValidation = validateTransitionQuantity(currentInventory, fromCondition, quantity);
    if (!quantityValidation.isValid) {
      throw new Error(quantityValidation.error);
    }
    
    // Calculate new quantities
    const newQuantities = calculateQuantitiesAfterTransition(
      currentInventory, 
      fromCondition, 
      toCondition, 
      quantity
    );
    
    // Update inventory
    await updateInventoryItem(currentInventory.id, newQuantities);
    
    // Create transition record
    const transitionRecord = createTransitionRecord({
      materialType,
      fromCondition,
      toCondition,
      quantity,
      reason,
      notes: options.notes || '',
      updatedBy: options.updatedBy || 'system',
      jobId: options.jobId || null,
      invoiceId: options.invoiceId || null
    });
    
    console.log(`✅ Transitioned ${quantity} ${materialType} from ${fromCondition} to ${toCondition}`);
    
    return {
      success: true,
      transitionRecord,
      updatedInventory: {
        ...currentInventory,
        ...newQuantities
      }
    };
  } catch (error) {
    console.error('Error transitioning item condition:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Process a sales transaction and update inventory
 * @param {Object} saleData - Sales transaction data
 * @returns {Promise<Object>} Sales processing result
 */
export const processSalesTransaction = async (saleData) => {
  try {
    // Get current inventory
    const items = await getInventoryItems({ materialType: saleData.materialType });
    if (items.length === 0) {
      throw new Error(`No inventory found for material type: ${saleData.materialType}`);
    }
    
    const currentInventory = items[0];
    
    // Validate sales transaction
    const validation = validateSalesTransaction(saleData, currentInventory);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }
    
    // Calculate inventory after sale
    const updatedInventory = calculateInventoryAfterSale(currentInventory, saleData);
    
    // Record sales transaction
    const salesRecord = await recordSalesTransaction(saleData);
    
    // Update inventory quantities
    await updateInventoryItem(currentInventory.id, updatedInventory);
    
    console.log(`✅ Processed sale of ${saleData.quantity} ${saleData.materialType} to ${saleData.customer}`);
    
    return {
      success: true,
      salesRecord,
      updatedInventory: {
        ...currentInventory,
        ...updatedInventory
      }
    };
  } catch (error) {
    console.error('Error processing sales transaction:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Calculate inventory status breakdown for a material type
 * @param {string} materialType - Material type
 * @param {string} startDate - Start date for status calculation (optional)
 * @param {string} endDate - End date for status calculation (optional)
 * @returns {Promise<Object>} Status breakdown
 */
export const calculateInventoryStatus = async (materialType, startDate = null, endDate = null) => {
  try {
    // Get inventory
    const items = await getInventoryItems({ materialType });
    if (items.length === 0) {
      return {
        materialType,
        statusBreakdown: {
          availableQuantity: 0,
          reservedQuantity: 0,
          unavailableQuantity: 0,
          totalUsableQuantity: 0,
          damagedQuantity: 0,
          statusPercentages: { available: 0, reserved: 0, unavailable: 0 }
        }
      };
    }
    
    const inventory = items[0];
    
    // Get reservations and job schedules
    const reservations = await getActiveReservations();
    const jobSchedules = []; // TODO: Integrate with job schedule service when available
    
    // Filter for this material type
    const materialReservations = reservations.filter(res => 
      res.reservedItems?.some(item => item.materialType === materialType)
    );
    
    // Calculate status breakdown
    const statusBreakdown = calculateStatusBreakdown(
      inventory,
      materialReservations,
      jobSchedules,
      new Date()
    );
    
    return {
      materialType,
      inventory,
      statusBreakdown
    };
  } catch (error) {
    console.error('Error calculating inventory status:', error);
    return {
      materialType,
      statusBreakdown: {
        availableQuantity: 0,
        reservedQuantity: 0,
        unavailableQuantity: 0,
        totalUsableQuantity: 0,
        damagedQuantity: 0,
        statusPercentages: { available: 0, reserved: 0, unavailable: 0 }
      },
      error: error.message
    };
  }
};

/**
 * Get inventory items with status and condition information
 * @param {Object} filters - Filter options
 * @returns {Promise<Array>} Array of inventory items with status
 */
export const getInventoryWithStatusAndCondition = async (filters = {}) => {
  try {
    const items = await getInventoryItems(filters);
    
    // Calculate status for each item
    const itemsWithStatus = await Promise.all(
      items.map(async (item) => {
        const statusInfo = await calculateInventoryStatus(item.materialType);
        return {
          ...item,
          statusBreakdown: statusInfo.statusBreakdown
        };
      })
    );
    
    return itemsWithStatus;
  } catch (error) {
    console.error('Error getting inventory with status:', error);
    return [];
  }
};

/**
 * Get usable quantity (new + used, excluding damaged)
 * @param {string} materialType - Material type
 * @returns {Promise<number>} Usable quantity
 */
export const getUsableQuantity = async (materialType) => {
  try {
    const items = await getInventoryItems({ materialType });
    if (items.length === 0) return 0;
    
    const inventory = items[0];
    return (inventory.newQuantity || 0) + (inventory.usedQuantity || 0);
  } catch (error) {
    console.error('Error getting usable quantity:', error);
    return 0;
  }
};

/**
 * Check availability for a quotation with multiple materials
 * @param {Object} requirements - Material requirements object
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Comprehensive availability check
 */
export const checkAvailabilityForQuotation = async (requirements, startDate, endDate) => {
  try {
    const availabilityResults = {};
    const shortages = [];
    let overallAvailable = true;
    
    // Check each material type
    for (const [materialType, requirement] of Object.entries(requirements)) {
      const availability = await checkMaterialAvailability(
        materialType,
        requirement.totalQuantity || requirement.quantity || 0,
        startDate,
        endDate
      );
      
      availabilityResults[materialType] = availability;
      
      if (!availability.isAvailable) {
        overallAvailable = false;
        shortages.push({
          materialType,
          required: availability.requiredQuantity,
          available: availability.availableQuantity,
          shortage: availability.shortage
        });
      }
    }
    
    return {
      available: overallAvailable,
      availabilityResults,
      shortages,
      checkedDateRange: { startDate, endDate },
      totalMaterialTypes: Object.keys(requirements).length
    };
  } catch (error) {
    console.error('Error checking quotation availability:', error);
    return {
      available: false,
      availabilityResults: {},
      shortages: [],
      checkedDateRange: { startDate, endDate },
      totalMaterialTypes: 0,
      error: error.message
    };
  }
};

/**
 * Get alternative suggestions for unavailable materials
 * @param {Object} requirements - Material requirements
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {Promise<Array>} Array of alternative suggestions
 */
export const getAlternativeSuggestions = async (requirements, startDate, endDate) => {
  try {
    const suggestions = [];
    
    // For now, return empty suggestions
    // This can be enhanced with business logic for material alternatives
    
    return suggestions;
  } catch (error) {
    console.error('Error getting alternative suggestions:', error);
    return [];
  }
};

// Export all constants for use in other components
export {
  INVENTORY_COLLECTION,
  CONFIGURATIONS_COLLECTION,
  RESERVATIONS_COLLECTION
};
